import type React from "react";
import type { ContentBlockWithLayout } from "../../../types/content-section.types";

import ImagesRenderer from "../images-renderer/ImagesRenderer";
import FAQBlock from "../faq-block/FAQBlock";
import FeaturePoints from "../feature-points/FeaturePoints";
import FeatureGrid from "../feature-grid/FeatureGrid";
import ParallaxHero from "../../hero/parallax-hero/ParallaxHero";
import IconTiles from "../../feature-tiles/icon-tiles/IconTiles";
import ContactUsTiles, { type ContactUsProps } from "../../contact/contact-us-tiles/ContactUsTiles";
import ContactUsBlock from "../../contact/contact-us-block/ContactUsBlock";
import SimpleContact from "../../contact/simple-contact/SimpleContact";
import StickyContact from "../../contact/sticky-contact/StickyContact";
import TrustSignals from "../../sidebar/trust-signals/TrustSignals";
import RelatedLinks from "../../sidebar/related-links/RelatedLinks";
import GoogleServiceArea from "../../external/google-service-area/GoogleServiceArea";
import GoogleReviewsServer from "../../external/google-reviews-server/GoogleReviewsServer";
import GoogleReviewsSticky from "../../external/google-reviews-sticky/GoogleReviewsSticky";
import CustomerReviews from "../../external/customer-reviews/CustomerReviews";
import ProductItemTiles from "../../products/ProductItemTiles/ProductItemTiles";
import ProductItemTilesSlider from "../../products/ProductItemTiles/ProductItemTilesSlider";
import ProductItemTilesCarousel from "../../products/ProductItemTiles/ProductItemTilesCarousel";
import ProductSliderScroll from "../../products/ProductSliderScroll/ProductSliderScroll";
import { BlockHeader } from "../content-article-reader/blocks/BlockHeader";
import type { BlockHeadingTag } from "../content-article-reader/blocks/BlockHeader";
import { HeroImage } from "../content-article-reader/blocks/HeroImage";
import { RichText } from "../content-article-reader/blocks/RichText";
import { Links } from "../content-article-reader/blocks/Links";
import { Videos } from "../content-article-reader/blocks/Videos";
import { Points } from "../content-article-reader/blocks/Points";
import {
    buildContainerStyle,
    buildOuterStyle,
    buildResponsiveCssVarRules,
    sanitizeId,
} from "./content-layout.utils";

type RendererProductItems =
    | React.ComponentProps<typeof ProductItemTiles>["items"]
    | React.ComponentProps<typeof ProductItemTilesSlider>["items"]
    | React.ComponentProps<typeof ProductItemTilesCarousel>["items"]
    | React.ComponentProps<typeof ProductSliderScroll>["items"];

type Props = {
    block: ContentBlockWithLayout;
    products?: RendererProductItems;
    contactUsProps?: ContactUsProps;
};

const RendererLink = ({
                          href,
                          children,
                          ...props
                      }: {
    href: string;
    children: React.ReactNode;
    className?: string;
    style?: React.CSSProperties;
    [key: string]: any;
}) => (
    <a href={href} {...props}>
        {children}
    </a>
);

const ContentBlockRenderer = ({ block, products, contactUsProps }: Props) => {
    if (!block || !block.type) return null;

    const id = block.id ?? block.type;
    const safeId = sanitizeId(id);

    const outerLayout = block.layout?.outer;
    const hasContainerWidth = !!outerLayout?.containerWidth;

    const responsiveCss = buildResponsiveCssVarRules(id, {
        outerPaddingY: outerLayout?.paddingY,
        outerPaddingX: outerLayout?.paddingX,
        containerWidth: outerLayout?.containerWidth,
    });

    const outerStyle = buildOuterStyle(
        id,
        outerLayout?.background,
        outerLayout?.style
    );

    const containerStyle = buildContainerStyle(id, hasContainerWidth);
    const containerClassName =
        outerLayout?.containerClassName ?? "mx-auto w-full";

    const hasTitle = "title" in block && typeof block.title === "string";
    const hasSubTitle =
        "subTitle" in block && typeof block.subTitle === "string";

    const renderBlockHeader = () => {
        if (!hasTitle && !hasSubTitle) return null;

        return (
            <BlockHeader
                title={hasTitle ? block.title : undefined}
                subTitle={hasSubTitle ? block.subTitle : undefined}
                HeadingTag={`h${block.headingLevel ?? 3}` as BlockHeadingTag}
            />
        );
    };

    const renderStandardBlockContent = () => (
        <>
            {renderBlockHeader()}

            {"heroImage" in block && block.heroImage ? (
                <HeroImage heroImage={block.heroImage} />
            ) : null}

            {"content" in block && block.content ? (
                <RichText html={block.content} />
            ) : null}

            {"images" in block && Array.isArray(block.images) && block.images.length ? (
                <ImagesRenderer images={block.images} />
            ) : null}

            {"links" in block && Array.isArray(block.links) && block.links.length ? (
                <Links links={block.links} />
            ) : null}

            {"videos" in block && Array.isArray(block.videos) && block.videos.length ? (
                <Videos videos={block.videos} />
            ) : null}

            {"points" in block && Array.isArray(block.points) && block.points.length ? (
                <Points points={block.points} />
            ) : null}

            {"faqs" in block && Array.isArray(block.faqs) && block.faqs.length ? (
                <FAQBlock items={block.faqs} />
            ) : null}

            {block.type === "featurePoints" && "featurePoints" in block && block.featurePoints ? (
                <FeaturePoints {...block.featurePoints} />
            ) : null}

            {block.type === "featureGrid" &&
            "featureGridBlock" in block &&
            block.featureGridBlock ? (
                <FeatureGrid {...block.featureGridBlock} />
            ) : null}
        </>
    );

    const renderBlockContent = () => {
        switch (block.type) {
            case "richText":
            case "hero":
            case "gallery":
            case "video":
            case "points":
            case "featurePoints":
            case "featureGrid":
            case "links":
            case "faq":
            case "mixed":
                return renderStandardBlockContent();

            case "parallaxHero":
                return "parallaxHero" in block && block.parallaxHero ? (
                    <ParallaxHero {...block.parallaxHero} />
                ) : null;

            case "iconTiles":
                return "iconTiles" in block && block.iconTiles ? (
                    <>
                        {renderBlockHeader()}
                        <IconTiles
                            dark={block.iconTiles.dark}
                            content={block.iconTiles.content}
                            titleClasses={block.iconTiles.titleClasses}
                            subtitleClass={block.iconTiles.subtitleClass}
                        />
                    </>
                ) : null;

            case "contactUsTiles": {
                const resolvedContactUsProps =
                    ("contactUsTiles" in block && block.contactUsTiles)
                        ? block.contactUsTiles
                        : contactUsProps;

                if (!resolvedContactUsProps) return null;

                return (
                    <>
                        {renderBlockHeader()}
                        <ContactUsTiles {...resolvedContactUsProps} />
                    </>
                );
            }

            case "contactUsBlock": {
                if (!("contactUsBlock" in block) || !block.contactUsBlock) {
                    return null;
                }

                const showInternalHeader =
                    block.contactUsBlock.showHeader ?? (!hasTitle && !hasSubTitle);

                return (
                    <>
                        {renderBlockHeader()}
                        <ContactUsBlock
                            {...block.contactUsBlock}
                            showHeader={showInternalHeader}
                        />
                    </>
                );
            }

            case "simpleContact":
                return "simpleContact" in block && block.simpleContact ? (
                    <>
                        {renderBlockHeader()}
                        <SimpleContact {...block.simpleContact} />
                    </>
                ) : null;

            case "stickyContact":
                return "stickyContact" in block && block.stickyContact ? (
                    <>
                        {renderBlockHeader()}
                        <StickyContact {...block.stickyContact} />
                    </>
                ) : null;

            case "trustSignals":
                return "trustSignals" in block && block.trustSignals ? (
                    <>
                        {renderBlockHeader()}
                        <TrustSignals {...block.trustSignals} />
                    </>
                ) : null;

            case "relatedLinks":
                return "relatedLinks" in block && block.relatedLinks ? (
                    <>
                        {renderBlockHeader()}
                        <RelatedLinks {...block.relatedLinks} />
                    </>
                ) : null;

            case "googleServiceArea":
                return "googleServiceArea" in block && block.googleServiceArea ? (
                    <>
                        {renderBlockHeader()}
                        <GoogleServiceArea {...block.googleServiceArea} />
                    </>
                ) : null;

            case "googleReviews":
                return "googleReviews" in block && block.googleReviews ? (
                    <>
                        {renderBlockHeader()}
                        <GoogleReviewsServer {...block.googleReviews} />
                    </>
                ) : null;

            case "googleReviewsSticky":
                return "googleReviewsSticky" in block && block.googleReviewsSticky ? (
                    <>
                        {renderBlockHeader()}
                        <GoogleReviewsSticky {...block.googleReviewsSticky} />
                    </>
                ) : null;

            case "customerReviews":
                return "customerReviews" in block && block.customerReviews ? (
                    <>
                        {renderBlockHeader()}
                        <CustomerReviews {...block.customerReviews} Link={RendererLink} />
                    </>
                ) : null;

            case "productItemTiles": {
                if (!("productItemTiles" in block) || !block.productItemTiles) return null;

                const resolvedItems =
                    Array.isArray((block.productItemTiles as any).items) &&
                    (block.productItemTiles as any).items.length > 0
                        ? (block.productItemTiles as any).items
                        : products ?? [];

                return (
                    <>
                        {renderBlockHeader()}
                        <ProductItemTiles
                            {...block.productItemTiles}
                            items={resolvedItems}
                            Link={RendererLink}
                        />
                    </>
                );
            }

            case "productItemTilesSlider": {
                if (!("productItemTilesSlider" in block) || !block.productItemTilesSlider) {
                    return null;
                }

                const resolvedItems =
                    Array.isArray((block.productItemTilesSlider as any).items) &&
                    (block.productItemTilesSlider as any).items.length > 0
                        ? (block.productItemTilesSlider as any).items
                        : products ?? [];

                return (
                    <>
                        {renderBlockHeader()}
                        <ProductItemTilesSlider
                            {...block.productItemTilesSlider}
                            items={resolvedItems}
                            Link={RendererLink}
                        />
                    </>
                );
            }

            case "productSliderScroll": {
                if (!("productSliderScroll" in block) || !block.productSliderScroll) {
                    return null;
                }

                const resolvedItems =
                    Array.isArray((block.productSliderScroll as any).items) &&
                    (block.productSliderScroll as any).items.length > 0
                        ? (block.productSliderScroll as any).items
                        : products ?? [];

                return (
                    <>
                        {renderBlockHeader()}
                        <ProductSliderScroll
                            {...block.productSliderScroll}
                            items={resolvedItems}
                            Link={RendererLink}
                        />
                    </>
                );
            }

            default:
                return null;
        }
    };

    const content = renderBlockContent();

    if (!content) return null;

    return (
        <section
            data-content-type={block.type}
            data-content-block-id={safeId}
            className={outerLayout?.className}
            style={outerStyle}
        >
            {responsiveCss ? <style>{responsiveCss}</style> : null}

            {hasContainerWidth ? (
                <div className={containerClassName} style={containerStyle}>
                    {content}
                </div>
            ) : (
                content
            )}
        </section>
    );
};

export default ContentBlockRenderer;
